import React, { useEffect, useId, useRef, useState } from "react";

import { Button } from "./ui/Button";
import { Input } from "./ui/Input";

const SEARCH_PLACEHOLDER = "changes by Ana to Orders last week";

type Props = {
  /** Applied search text from the page URL. */
  q: string;
  /** Reading of the API `interpreted` object. Empty when `q` is empty. */
  reading: string;
  /** API `message` for a rejected search. Null when the last load was ok. */
  error: string | null;
  onSearch: (q: string) => void;
};

/**
 * Search box above the Activity filter bar (MOSA-933).
 *
 * Enter or the Search button applies the text. Typing does not.
 */
export const ActivitySearchBox: React.FC<Props> = ({
  q,
  reading,
  error,
  onSearch,
}) => {
  const inputId = useId();
  const errorId = useId();
  const readingId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState(q);
  const [syncedQ, setSyncedQ] = useState(q);

  // Clear filters and Back/Forward change the applied text. Keep a typed
  // draft until that happens.
  if (q !== syncedQ) {
    setSyncedQ(q);
    setDraft(q);
  }

  useEffect(() => {
    if (error) inputRef.current?.focus();
  }, [error]);

  const describedBy = [
    error ? errorId : "",
    reading ? readingId : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <form
      role="search"
      className="space-y-2"
      aria-label="Search"
      onSubmit={(event) => {
        event.preventDefault();
        onSearch(draft);
      }}
    >
      <div className="flex flex-wrap items-end gap-2">
        <label
          htmlFor={inputId}
          className="flex min-w-[12rem] flex-1 flex-col gap-1 text-xs text-od-muted"
        >
          <span>Search</span>
          <Input
            ref={inputRef}
            id={inputId}
            type="text"
            size="sm"
            value={draft}
            placeholder={SEARCH_PLACEHOLDER}
            autoComplete="off"
            enterKeyHint="search"
            spellCheck={false}
            aria-invalid={error ? true : undefined}
            aria-describedby={describedBy || undefined}
            onChange={(event) => setDraft(event.target.value)}
          />
        </label>
        <Button type="submit" variant="secondary" size="sm">
          Search
        </Button>
      </div>
      <p
        id={readingId}
        role="status"
        className={reading ? "text-sm text-od-muted" : "sr-only"}
      >
        {reading}
      </p>
      {error && (
        <p id={errorId} role="alert" className="text-sm text-od-warning">
          {error}
        </p>
      )}
    </form>
  );
};
